import { useState } from "react";
import {
  Modal,
  Group,
  Alert,
  List,
  Box,
  Stack,
  Text,
  Button,
  ThemeIcon,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconFile,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import CSVDropzoneButton from "../../CSVDropzone";
import { toBase64 } from "../../../services/helper";
import { useImportInvites } from "../../../api/invites";

// Import recipients from a CSV/XLSX file. The file is base64-encoded and parsed
// server-side; the summary reports added / skipped (duplicates) / invalid. Used
// as a modal opened from the Invitees toolbar (mirrors the translations import).
const ImportRecipientsModal = ({ opened, onClose, eventId }) => {
  const [file, setFile] = useState(null);

  const {
    mutate: importInvites,
    isPending,
    data: result,
    isSuccess,
    reset,
  } = useImportInvites(eventId);

  const handleImport = async () => {
    const data = await toBase64(file);
    importInvites({ file: { name: file.name, type: file.type, data } });
  };

  const clearFile = () => {
    setFile(null);
    reset();
  };

  const handleClose = () => {
    clearFile();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Import recipients"
      centered
      size="lg"
    >
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="File requirements"
        color="blue"
        variant="light"
      >
        <List size="sm">
          <List.Item>File must be in .csv or .xlsx format</List.Item>
          <List.Item>File size must be less than 10MB</List.Item>
          <List.Item>Required column: email</List.Item>
          <List.Item>Optional columns: first name, last name</List.Item>
          <List.Item>
            Re-importing merges: new emails are added, existing ones skipped
          </List.Item>
        </List>
      </Alert>

      <Box my="md">
        {!file ? (
          <CSVDropzoneButton onDrop={setFile} />
        ) : (
          <Stack gap="xs" align="center">
            <Group>
              <ThemeIcon size="xl" variant="light" radius="xl" color="blue">
                <IconFile size={24} />
              </ThemeIcon>
              <Stack gap={0}>
                <Text fw={500}>{file.name}</Text>
                <Text size="xs" c="dimmed">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </Text>
              </Stack>
            </Group>
            <Group mt={8} grow w="100%">
              <Button
                leftSection={<IconCheck size={16} />}
                onClick={handleImport}
                disabled={isSuccess}
                loading={isPending}
              >
                Import file
              </Button>
              <Button
                leftSection={<IconX size={16} />}
                onClick={clearFile}
                variant="outline"
                color="red"
                disabled={isPending}
              >
                Clear file
              </Button>
            </Group>
          </Stack>
        )}
      </Box>

      {isSuccess && result && (
        <Alert
          icon={<IconCheck size={16} />}
          title="Import complete"
          color={result.invalid > 0 ? "yellow" : "green"}
          variant="light"
        >
          {result.added} added · {result.skipped} skipped (duplicates) ·{" "}
          {result.invalid} invalid
        </Alert>
      )}
    </Modal>
  );
};

export default ImportRecipientsModal;
